import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { formatCurrency } from '../../utils/format';
import { showAlert } from '../../services/dialogService';
import './AddLimitFlow.css';

export interface LimitData {
  category: string;
  amount: number;
  period_months: 1 | 3 | 6 | 12;
}

interface AddLimitFlowProps {
  onClose: () => void;
  onSubmit: (limitData: LimitData) => Promise<void>;
  availableCategories: string[];
  existingLimits: Array<{ category_name: string; limit_amount: number; period_months?: 1 | 3 | 6 | 12 }>;
  editingLimit?: { category_name: string; limit_amount: number; period_months: number } | null;
  currency?: string;
  currencyCode?: string;
}

export const AddLimitFlow: React.FC<AddLimitFlowProps> = ({
  onClose,
  onSubmit,
  availableCategories,
  existingLimits,
  editingLimit,
  currency = 'RUB',
  currencyCode = 'RUB'
}) => {
  // Если редактируем, сразу переходим к вводу суммы с предзаполненными данными
  const [step, setStep] = useState<'category' | 'period' | 'amount'>(
    editingLimit ? 'amount' : 'category'
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(
    editingLimit?.category_name || ''
  );
  const [periodMonths, setPeriodMonths] = useState<1 | 3 | 6 | 12>(
    (editingLimit?.period_months as 1 | 3 | 6 | 12) || 1
  );
  const [amount, setAmount] = useState<string>(
    editingLimit ? editingLimit.limit_amount.toString() : ''
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Блокируем скролл body когда модалка открыта
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      // Восстанавливаем скролл при закрытии
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  // Логируем полученные категории
  console.log('🏷️ AddLimitFlow received categories:', availableCategories);
  console.log('📊 Existing limits:', existingLimits);

  // Фильтруем категории, для которых еще нет лимита
  const categoriesWithoutLimit = availableCategories.filter(
    cat => !existingLimits.some(limit => limit.category_name === cat)
  );

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    
    // Если для категории уже есть лимит, устанавливаем его значение
    const existingLimit = existingLimits.find(l => l.category_name === category);
    if (existingLimit) {
      setAmount(existingLimit.limit_amount.toString());
      // Если у существующего лимита есть период, устанавливаем его
      if (existingLimit.period_months) {
        setPeriodMonths(existingLimit.period_months);
      }
    }
    
    setStep('period');
  };

  const handlePeriodSelect = (months: 1 | 3 | 6 | 12) => {
    setPeriodMonths(months);
    setStep('amount');
  };

  const getPeriodLabel = (months: number): string => {
    switch(months) {
      case 1: return '1 месяц';
      case 3: return '3 месяца';
      case 6: return '6 месяцев';
      case 12: return '1 год';
      default: return `${months} месяцев`;
    }
  };

  const getPeriodShortLabel = (months: number): string => {
    switch(months) {
      case 1: return '1 мес';
      case 3: return '3 мес';
      case 6: return '6 мес';
      case 12: return '1 год';
      default: return `${months} мес`;
    }
  };

  const handleAmountChange = (value: string) => {
    // Разрешаем только цифры и точку/запятую
    const sanitized = value.replace(/[^\d.,]/g, '').replace(',', '.');
    setAmount(sanitized);
  };

  const handleSubmit = async () => {
    const numericAmount = parseFloat(amount);
    
    if (!selectedCategory || isNaN(numericAmount) || numericAmount <= 0) {
      await showAlert('Пожалуйста, введите корректную сумму', { 
        type: 'warning', 
        title: 'Некорректная сумма' 
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit({ 
        category: selectedCategory, 
        amount: numericAmount,
        period_months: periodMonths
      });
      // Закрытие модалки теперь контролируется родительским компонентом
    } catch (error) {
      console.error('Error submitting limit:', error);
      // Ошибка уже обработана в родительском компоненте
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    // Если редактируем, сразу закрываем при нажатии назад
    if (editingLimit && step === 'amount') {
      onClose();
      return;
    }
    
    if (step === 'amount') {
      setStep('period');
      setAmount('');
    } else if (step === 'period') {
      setStep('category');
      setSelectedCategory('');
      setPeriodMonths(1);
    } else {
      onClose();
    }
  };

  return createPortal(
    <div className="add-limit-flow">
      <div className="add-limit-flow__overlay" onClick={onClose} />
      <div className="add-limit-flow__content">
        {/* Header */}
        <div className="add-limit-flow__header">
          <button className="add-limit-flow__back" onClick={handleBack}>
            <Icon name="chevron_left" size={24} />
          </button>
          <h2 className="add-limit-flow__title">
            {step === 'category' && 'Выберите категорию'}
            {step === 'period' && 'Выберите период'}
            {step === 'amount' && (editingLimit ? 'Изменить лимит' : 'Установите лимит')}
          </h2>
          <button className="add-limit-flow__close" onClick={onClose}>
            <Icon name="close" size={24} />
          </button>
        </div>

        {/* Step: Category Selection */}
        {step === 'category' && (
          <div className="add-limit-flow__step">
            <div className="limit-categories">
              {availableCategories.length === 0 ? (
                <div className="limit-categories__empty">
                  <p>Нет доступных категорий</p>
                </div>
              ) : (
                availableCategories.map((category) => {
                  const existingLimit = existingLimits.find(l => l.category_name === category);
                  return (
                    <button
                      key={category}
                      className="limit-category-item neo-raised"
                      onClick={() => handleCategorySelect(category)}
                    >
                      <div className="limit-category-item__content">
                        <span className="limit-category-item__name">{category}</span>
                        {existingLimit && (
                          <span className="limit-category-item__current">
                            Текущий: {formatCurrency(existingLimit.limit_amount, currency)}
                          </span>
                        )}
                      </div>
                      <Icon name="chevron-right" size={20} color="#8E8E93" />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Step: Period Selection */}
        {step === 'period' && (
          <div className="add-limit-flow__step">
            <div className="period-selector">
              <button
                className="period-option neo-raised"
                onClick={() => handlePeriodSelect(1)}
              >
                <div className="period-option__icon">
                  <Icon name="calendar" size={32} color="var(--color-primary)" />
                </div>
                <div className="period-option__label">1 месяц</div>
              </button>
              <button
                className="period-option neo-raised"
                onClick={() => handlePeriodSelect(3)}
              >
                <div className="period-option__icon">
                  <Icon name="calendar" size={32} color="var(--color-primary)" />
                </div>
                <div className="period-option__label">3 месяца</div>
              </button>
              <button
                className="period-option neo-raised"
                onClick={() => handlePeriodSelect(6)}
              >
                <div className="period-option__icon">
                  <Icon name="calendar" size={32} color="var(--color-primary)" />
                </div>
                <div className="period-option__label">6 месяцев</div>
              </button>
              <button
                className="period-option neo-raised"
                onClick={() => handlePeriodSelect(12)}
              >
                <div className="period-option__icon">
                  <Icon name="bar-chart" size={32} color="var(--color-primary)" />
                </div>
                <div className="period-option__label">1 год</div>
              </button>
            </div>
          </div>
        )}

        {/* Step: Amount Input */}
        {step === 'amount' && (
          <div className="add-limit-flow__step">
            <div className="limit-amount-input">
              <p className="limit-amount-input__label">
                Лимит на {getPeriodShortLabel(periodMonths)} для категории "{selectedCategory}"
              </p>
              
              <div className="limit-amount-input__field">
                <input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="0"
                  className="limit-amount-input__input"
                />
                <span className="limit-amount-input__currency">{currency}</span>
              </div>

              <p className="limit-amount-input__hint">
                Вы получите уведомление при достижении 70% и 100% от лимита
              </p>
            </div>

            <div className="add-limit-flow__actions">
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!amount || parseFloat(amount) <= 0 || isSubmitting}
                fullWidth
                className="neo-raised"
              >
                {isSubmitting ? 'Сохранение...' : (editingLimit ? 'Изменить лимит' : 'Установить лимит')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
